// storybook-check-ignore
import * as React from 'react';
import Cookies from 'js-cookie';
import { analytics, globalObservability } from '@opendoor/observability/slim';

import AccountForm, { IAccountFormProps } from '@opendoor/cinderblocks/platform/auth/AccountForm';
import LogInConnector, { AccountProps } from '../shared/AccountConnector';
import { REDIRECT_URLS_REGEX } from '../globals';
import { Flex, BoxProps } from '@opendoor/bricks/core';
import { breakpoints } from '@opendoor/bricks/theme/eero';
import useExclusiveAnalytics, { ExclusivesFeatureName } from './hooks/useExclusiveAnalytics';
import { isValidRelativeRedirectTo } from '../../helpers/exclusives/redirectTo';
import { usePromptGoogleOneTap } from './hooks/usePromptGoogleOneTap';
import { setShouldPresentMarketModalOnLS } from './MarketSelectorModal';

const Sentry = globalObservability.getSentryClient();

const UNAUTHORIZED_STATUS_CODE = 401;

export interface Props extends AccountProps {
  redirectTo?: string;
  emailPrefill?: string;
  onSubmit?: any;
  boxProps?: BoxProps;
  loginHeaderText?: string;
  registrationHeaderText?: string;
  loginSubHeaderText?: string;
  trackFeatureName?: ExclusivesFeatureName;
  accountFormProps?: Partial<IAccountFormProps>;
  accountFormRef?: React.Ref<AccountForm>;
  disableLoadTracking?: boolean;
  disablePromptGoogleOneTap?: boolean;
}

function ExclusivesAccountFormPage({
  trackFeatureName = 'exclusives-login',
  accountFormRef,
  ...props
}: Props) {
  const { trackExclusivesEvent } = useExclusiveAnalytics();
  function reportEvent<T>(label: string, additionalContextualData: T) {
    trackExclusivesEvent(trackFeatureName, label, undefined, additionalContextualData);
  }
  usePromptGoogleOneTap(props, 'login', {
    disablePromptGoogleOneTap: props.disablePromptGoogleOneTap,
  });
  // componentDidMount
  React.useEffect(() => {
    const { email, redirectTo, emailPrefill, disableLoadTracking } = props;
    analytics.register({ login: Cookies.get('login-test') || 'no-cookie' });
    if (!disableLoadTracking) {
      reportEvent('load', {
        email,
        redirectTo,
        emailPrefill,
      });
    }
  }, []);

  // Redirect when unauthenticated
  React.useEffect(() => {
    if (props.loggedIn && props.infoIsLoaded) {
      redirectIfLoggedIn(props);
    }
  }, [props.infoIsLoaded, props.loggedIn]);

  // Track error when the authentication has failed
  React.useEffect(() => {
    if (!props.infoIsLoading) {
      const { loginRequestStatus } = props;
      if (
        loginRequestStatus &&
        loginRequestStatus.error &&
        loginRequestStatus.statusCode !== UNAUTHORIZED_STATUS_CODE
      ) {
        Sentry.captureException?.(loginRequestStatus.error);
      }
    }
  }, [props.infoIsLoading]);

  function redirectIfLoggedIn(props: Props) {
    const { loggedIn, email, redirectTo, serverLoginRedirect } = props;
    if (loggedIn) {
      setShouldPresentMarketModalOnLS(true);
      if (props.onSubmit) {
        props.onSubmit(email);
      } else {
        reportEvent('redirect', {
          email,
          redirectTo,
          serverLoginRedirect,
        });
        let redirectUrl;
        if (!redirectTo && serverLoginRedirect && serverLoginRedirect !== '/') {
          redirectUrl = serverLoginRedirect;
        } else if (redirectTo) {
          // allow redirection to relative urls & opendoor.com absolute urls
          if (REDIRECT_URLS_REGEX.test(redirectTo) || isValidRelativeRedirectTo(redirectTo)) {
            // absolute path
            redirectUrl = redirectTo;
          }
        }
        window.location.assign(redirectUrl || '/');
      }
    }
  }

  const {
    logIn,
    register,
    requestHumanStatus,
    hasGeneratedPassword,
    isNewHuman,
    loginRequestStatus,
    humanStatusRequestStatus,
    registrationRequestStatus,
    email,
    emailPrefill,
    requestResetPassword,
    loginSubHeaderText,
    loginHeaderText = 'Sign in or create an account',
    registrationHeaderText = 'Create an Opendoor account',
  } = props;

  const onHumanStatusRequest = (email: string, fullName?: string) => {
    requestHumanStatus(email);
    props.accountFormProps?.onHumanStatusRequest?.(email, fullName);
  };

  return (
    <Flex
      maxWidth={breakpoints.slice(1)}
      mx="auto"
      justifyContent="center"
      mb={[11, 12]}
      mt={[0, 0, '4rem']}
      flex="1"
      height="100%"
      padding={[7, 7, 0]}
      {...props.boxProps}
    >
      <AccountForm
        ref={accountFormRef}
        showGoogleAuth
        showFacebookAuth
        {...props.accountFormProps}
        analyticsName="cosmos-exclusives-account-form"
        loginSubHeaderText={loginSubHeaderText}
        loginHeaderText={loginHeaderText}
        registrationHeaderText={registrationHeaderText}
        email={email || emailPrefill}
        hasGeneratedPassword={hasGeneratedPassword}
        error={
          loginRequestStatus.error ||
          humanStatusRequestStatus.error ||
          registrationRequestStatus.error
        }
        passwordLoading={loginRequestStatus.isLoading || registrationRequestStatus.isLoading}
        emailLoading={humanStatusRequestStatus.isLoading}
        isNewHuman={isNewHuman}
        onLogin={logIn}
        overrideHasSubmittedEmailCheckForLogin={!!emailPrefill}
        onRegister={register}
        onHumanStatusRequest={onHumanStatusRequest}
        requestResetPassword={requestResetPassword}
        phoneNumberInputOptional={false}
        containerProps={{ padding: 0 }}
      />
    </Flex>
  );
}

export default LogInConnector(ExclusivesAccountFormPage);
